// Pure SEO computation used at RUNTIME by SeoHead and at BUILD-TIME by the
// prerender script. This module deliberately has ZERO content imports so the
// client bundle stays small.

export const SITE = "https://ghio.nl";
// Wide (1200×630) OG default for every route that has no category-specific
// hero. Landing pages (/, /support, /blog and their /en/ mirrors) all share
// this — otherwise LinkedIn/Facebook fall back to a tiny thumbnail card
// because ghio-logo.png (400×121) is below their 600px large-card threshold.
export const DEFAULT_IMAGE = `${SITE}/assets/og-default.png`;
// Absolute last-resort fallback if og-default.png is ever unreachable.
const LOGO_FALLBACK = `${SITE}/assets/ghio-logo.png`;
// Assets we KNOW are 1200×630 — matches the hero-*.png set plus og-default.png.
const WIDE_ASSET_RE = /\/assets\/(hero-[a-z]+|og-default)\.png$/i;

export function computeSeo({
  lang,
  nlPath,
  enPath,
  title,
  description,
  image,
  imageWidth: forcedWidth,
  imageHeight: forcedHeight,
  ogType = "website",
  jsonLd = null,
  noindex = false,
}) {
  const currentPath = lang === "en" ? enPath || nlPath : nlPath;
  const canonical = `${SITE}${currentPath}`;
  const ogLocale = lang === "en" ? "en_US" : "nl_NL";
  const ogLocaleAlt = lang === "en" ? "nl_NL" : "en_US";
  const htmlLang = lang === "en" ? "en" : "nl";

  // Normalize the og:image URL. heroForCategory() (content.js) is now the
  // single source of image URLs for blog posts and always returns /assets/*,
  // so no wp-content sanitisation is needed here — kept simple.
  const rawImage = image || DEFAULT_IMAGE;
  const safeImage = rawImage.startsWith("http")
    ? rawImage
    : `${SITE}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`;

  // Image dimensions must match the actual asset — Facebook/LinkedIn read
  // og:image:width/height BEFORE fetching the image and downgrade to a small
  // thumbnail card when the advertised size is <600px on either side.
  //  - /assets/hero-*.png and /assets/og-default.png are 1200×630.
  //  - Anything else (rare — only /assets/ghio-logo.png last-resort) is 400×121.
  const isWide = WIDE_ASSET_RE.test(safeImage);
  const imageWidth = forcedWidth || (isWide ? 1200 : 400);
  const imageHeight = forcedHeight || (isWide ? 630 : 121);
  // alt = the page title (spec: not the company name).
  const imageAlt = title;

  const alternates = [{ hreflang: "nl-NL", href: `${SITE}${nlPath}` }];
  if (enPath) alternates.push({ hreflang: "en", href: `${SITE}${enPath}` });
  alternates.push({ hreflang: "x-default", href: `${SITE}${nlPath}` });

  const stamp = (n) =>
    n && typeof n === "object" && !Array.isArray(n) && n["@type"]
      ? { ...n, inLanguage: htmlLang }
      : n;
  const stampedJsonLd = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd.map(stamp)
      : stamp(jsonLd)
    : null;

  return {
    htmlLang,
    title,
    description,
    canonical,
    robots: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    alternates,
    ogType,
    ogUrl: canonical,
    ogTitle: title,
    ogDescription: description,
    ogImage: safeImage,
    ogImageWidth: imageWidth,
    ogImageHeight: imageHeight,
    ogImageAlt: imageAlt,
    ogLocale,
    ogLocaleAlternate: enPath ? ogLocaleAlt : null,
    twitterCard: "summary_large_image",
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: safeImage,
    twitterImageAlt: imageAlt,
    jsonLd: stampedJsonLd,
  };
}
